import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Heading,
  List,
  ListItem,
  ListIcon,
  Select,
  Divider,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircleIcon, ChevronDownIcon } from '@chakra-ui/icons';

const UpgradeModal = ({ isOpen, onClose, isAdditionalDeposit = false }) => {
  const [selectedTier, setSelectedTier] = useState('1500');
  
  // Color values from the project's palette
  const primaryColor = "#6B21A8"; // Deep purple
  const secondaryColor = "#9333EA"; // Medium purple 
  const textColor = "#1F2937";
  const lightPurple = "#F3E8FF"; // Light purple for backgrounds
  
  // Button styles
  const buttonBg = primaryColor;
  const buttonHoverBg = "#7C3AED"; // Slightly lighter purple on hover
  
  const depositTiers = [
    { value: '500', label: '$500', buyingPower: '$5,000', shopifyVariant: '50407519453468' },
    { value: '1000', label: '$1,000', buyingPower: '$10,000', shopifyVariant: '50691506635036' },
    { value: '1500', label: '$1,500', buyingPower: '$15,000', shopifyVariant: '44705841053980' },
    { value: '2000', label: '$2,000', buyingPower: '$20,000', shopifyVariant: '44705841086748' },
    { value: '3000', label: '$3,000', buyingPower: '$30,000', shopifyVariant: '44705841119516' },
    { value: '4000', label: '$4,000', buyingPower: '$40,000', shopifyVariant: '44705841152284' },
    { value: '5000', label: '$5,000', buyingPower: '$50,000', shopifyVariant: '46837452734748' },
  ];

  const SHOPIFY_STORE_URL = 'https://shop.importjdmmagic.com';

  const handleTierChange = (e) => {
    setSelectedTier(e.target.value);
  };

  const goToShopify = (depositAmount) => {
    const tier = depositTiers.find(t => t.value === depositAmount);
    const url = tier
      ? `${SHOPIFY_STORE_URL}/cart/${tier.shopifyVariant}:1`
      : `${SHOPIFY_STORE_URL}/products/vehicle-deposit`;
    window.open(url, '_blank');
  };

  const selectedTierInfo = depositTiers.find(tier => tier.value === selectedTier);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="rgba(255, 255, 255, 0.5)" backdropFilter="blur(5px)" />
      <ModalContent 
        borderRadius="md" 
        boxShadow="xl"
        bg={useColorModeValue('white', 'gray.800')}
        maxW="550px"
      >
        <ModalHeader 
          borderBottomWidth="1px" 
          borderColor={lightPurple}
          color={primaryColor}
          fontSize="xl"
          fontWeight="bold"
          py={3}
          bg={`${primaryColor}08`}
        >
          {isAdditionalDeposit ? 'Make Additional Deposit' : 'Upgrade Your Account'}
        </ModalHeader>
        <ModalCloseButton 
          color={primaryColor}
          size="lg"
          bg="white"
          borderRadius="full"
          border="2px solid"
          borderColor={lightPurple}
          _hover={{
            bg: lightPurple,
            transform: "rotate(90deg)"
          }}
          transition="all 0.2s ease"
        />
        
        <ModalBody py={4}>
          <Text 
            mb={4} 
            fontSize="sm" 
            color={textColor}
            lineHeight="tall"
          >
            {isAdditionalDeposit
              ? 'Select an amount to add to your deposit. Your bidding limit will increase to 10x your total deposit.'
              : 'To gain access to the Japanese vehicle auctions and inspections, a deposit is required. The amount (10% of vehicle cost) is refundable or applied to your purchase.'
            }
          </Text>
          
          <Box mb={4}>
            <Heading 
              size="sm" 
              mb={2} 
              color={primaryColor}
              fontWeight="semibold"
            >
              Select Your Deposit Amount
            </Heading>
            <Select 
              value={selectedTier} 
              onChange={handleTierChange}
              size="md"
              mb={2}
              borderColor={lightPurple}
              focusBorderColor={secondaryColor}
              _hover={{ borderColor: secondaryColor }}
              color={textColor}
              icon={<ChevronDownIcon color={secondaryColor} />}
              bg={`${lightPurple}60`}
            >
              {depositTiers.map((tier) => (
                <option key={tier.value} value={tier.value}>
                  {tier.label}
                </option>
              ))}
            </Select>
          </Box>
          
          <Divider borderColor={`${primaryColor}20`} mb={4} />
          
          <Box mb={4}>
            <Heading 
              size="sm" 
              mb={2} 
              color={primaryColor}
              fontWeight="semibold"
            >
              Your Buying Power
            </Heading>
            <Box 
              p={3} 
              bg={lightPurple} 
              borderRadius="md"
              borderLeft="4px solid" 
              borderColor={secondaryColor}
              boxShadow="sm"
            >
              <Heading 
                size="md" 
                color={primaryColor}
                fontWeight="bold"
                display="flex"
                alignItems="center"
              >
                {selectedTierInfo?.buyingPower}
                <Text 
                  fontSize="sm" 
                  fontWeight="medium" 
                  color={secondaryColor}
                  ml={2}
                >
                  (Requires {selectedTierInfo?.label} deposit)
                </Text>
              </Heading>
              <Text mt={1} fontSize="sm" color={textColor}>
                Maximum vehicle value you can bid on with this deposit tier
              </Text>
            </Box>
          </Box>
          
          {!isAdditionalDeposit && (
            <>
              <Divider borderColor={`${primaryColor}20`} mb={4} />
              <Box mb={3}>
                <Heading 
                  size="sm" 
                  mb={2} 
                  color={primaryColor}
                  fontWeight="semibold"
                >
                  Benefits Included
                </Heading>
                <VStack align="stretch" spacing={1}>
                  <List spacing={1}>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={CheckCircleIcon} color={secondaryColor} boxSize={4} mr={2} />
                      <Text fontSize="sm" color={textColor}>Unlocks real-time auction listings</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={CheckCircleIcon} color={secondaryColor} boxSize={4} mr={2} />
                      <Text fontSize="sm" color={textColor}>Shows auction times</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={CheckCircleIcon} color={secondaryColor} boxSize={4} mr={2} />
                      <Text fontSize="sm" color={textColor}>Enables inspection requests</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={CheckCircleIcon} color={secondaryColor} boxSize={4} mr={2} />
                      <Text fontSize="sm" color={textColor}>Sets your bidding limit to your maximum buying power</Text>
                    </ListItem>
                    <ListItem display="flex" alignItems="center">
                      <ListIcon as={CheckCircleIcon} color={secondaryColor} boxSize={4} mr={2} />
                      <Text fontSize="sm" color={textColor}>Access to price estimator tool</Text>
                    </ListItem>
                  </List>
                </VStack>
              </Box>
            </>
          )}
        </ModalBody>
        
        <ModalFooter 
          borderTopWidth="1px" 
          borderColor={lightPurple}
          py={3}
          bg={`${primaryColor}05`}
          display="flex"
          justifyContent="center"
        >
          <Button 
            colorScheme="purple" 
            bg={buttonBg}
            color="white"
            size="md"
            fontWeight="bold"
            px={6}
            py={5}
            fontSize="16px"
            borderRadius="full"
            boxShadow="none"
            _hover={{ 
              bg: buttonHoverBg,
              transform: "translateY(-2px)"
            }}
            _active={{
              bg: "#5B1999",
              transform: "translateY(0)"
            }}
            transition="all 0.2s ease"
            onClick={() => goToShopify(selectedTier)}
            width={{ base: "80%", md: "auto" }}
          >
            Make ${selectedTier} Deposit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpgradeModal; 